import React, { useState, useCallback, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, IconButton, Box, TextField, Button,
  InputAdornment, Card, CardContent, Typography, TableContainer, Table,
  TableHead, TableRow, TableCell, TableBody, Paper, MenuItem, TablePagination,
  useTheme, Theme, CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import { EquipmentType, Meta } from '../types';
import { equipmentApi } from '../services/api';
import ConfirmDialog from './ConfirmDialog';
import { useSnackbar } from 'notistack';
import { useConfirmDialog } from '../hooks/UseConfirmDialog';
import { gradientButtonSx } from './SharedPageParts';
import { unwrapListResponse, unwrapMetaResponse } from '../apiUnwrapper/apiUnwrapper';

const FAMILY_OPTIONS = ['HAUL TRUCK', 'LOADING UNIT', 'AUXILIARY UNIT'] as const;
const DEFAULT_META: Meta = { total: 0, size: 5, page: 1 };

interface Props {
  open: boolean;
  onClose: () => void;
}

type EditTarget = EquipmentType | null;

const ManageEquipmentTypesPopUp: React.FC<Props> = ({ open, onClose }) => {
  const theme: Theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const { confirmState, confirm, closeConfirm, executeConfirm } = useConfirmDialog();

  const [typesList, setTypesList]   = useState<EquipmentType[]>([]);
  const [meta, setMeta]             = useState<Meta>(DEFAULT_META);
  const [loading, setLoading]       = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [newTypeName, setNewTypeName] = useState('');
  const [newTypeFamily, setNewTypeFamily] = useState('');
  const [editTarget, setEditTarget] = useState<EditTarget>(null);

  // ── Data loading ──────────────────────────────────────────────────────────

  const loadTypes = useCallback(async (page = 1, size = DEFAULT_META.size) => {
    try {
      setLoading(true);
      const res = await equipmentApi.getEquipmentTypes(page, size);
      setTypesList(unwrapListResponse<EquipmentType>(res));
      setMeta(unwrapMetaResponse<Meta>(res) ?? DEFAULT_META);
    } catch (err) {
      enqueueSnackbar(`Failed to load equipment types: ${err}`, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  const searchTypes = useCallback(async (term: string, page = 1, size = DEFAULT_META.size) => {
    try {
      setLoading(true);
      const res = await equipmentApi.getEquipmentTypesForSearchTerm(term, page, size);
      setTypesList(unwrapListResponse<EquipmentType>(res));
      setMeta(unwrapMetaResponse<Meta>(res) ?? DEFAULT_META);
    } catch (err) {
      enqueueSnackbar(`Failed to search equipment types: ${err}`, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    if (open) loadTypes();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Pagination & search ───────────────────────────────────────────────────

  const handleSearch = useCallback(() => {
    if (searchTerm.trim()) searchTypes(searchTerm, 1, meta.size);
    else loadTypes(1, meta.size);
  }, [searchTerm, meta.size, searchTypes, loadTypes]);

  const handlePageChange = useCallback((_: unknown, newPage: number) => {
    if (searchTerm.trim()) searchTypes(searchTerm, newPage + 1, meta.size);
    else loadTypes(newPage + 1, meta.size);
  }, [searchTerm, meta.size, searchTypes, loadTypes]);

  const handleRowsPerPageChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const size = parseInt(e.target.value, 10);
    if (searchTerm.trim()) searchTypes(searchTerm, 1, size);
    else loadTypes(1, size);
  }, [searchTerm, searchTypes, loadTypes]);

  // ── After any mutation — just reload the current view ────────────────────

  const afterMutation = useCallback(async () => {
    if (searchTerm.trim()) await searchTypes(searchTerm, meta.page, meta.size);
    else await loadTypes(meta.page, meta.size);
  }, [searchTerm, meta.page, meta.size, searchTypes, loadTypes]);

  // ── Add ───────────────────────────────────────────────────────────────────

  const handleAdd = useCallback(async () => {
    if (!newTypeName.trim() || !newTypeFamily.trim()) return;
    try {
      await equipmentApi.addEquipmentType({ id: 0, equipmentName: newTypeName, equipmentTypeFamily: newTypeFamily });
      enqueueSnackbar('Equipment type created successfully', { variant: 'success' });
      setNewTypeName('');
      setNewTypeFamily('');
      setShowAddForm(false);
      await afterMutation();
    } catch (err) {
      enqueueSnackbar(`Error creating equipment type: ${err}`, { variant: 'error' });
    }
  }, [newTypeName, newTypeFamily, afterMutation, enqueueSnackbar]);

  // ── Update ────────────────────────────────────────────────────────────────

  const handleUpdate = useCallback(async () => {
    if (!editTarget) return;
    try {
      await equipmentApi.addEquipmentType({
        id: editTarget.id,
        equipmentName: editTarget.equipmentName,
        equipmentTypeFamily: editTarget.equipmentTypeFamily,
      });
      enqueueSnackbar('Equipment type updated successfully', { variant: 'success' });
      setEditTarget(null);
      await afterMutation();
    } catch (err) {
      enqueueSnackbar(`Error updating equipment type: ${err}`, { variant: 'error' });
    }
  }, [editTarget, afterMutation, enqueueSnackbar]);

  // ── Delete ────────────────────────────────────────────────────────────────

  const handleDeleteRequest = useCallback((id: number) => {
    confirm('Are you sure you want to delete this equipment type?', async () => {
      try {
        await equipmentApi.deleteEquipmentType(id);
        enqueueSnackbar('Equipment type deleted successfully', { variant: 'success' });
        await afterMutation();
      } catch (err) {
        enqueueSnackbar(`Error deleting equipment type: ${err}`, { variant: 'error' });
      }
    });
  }, [confirm, enqueueSnackbar, afterMutation]);

  // ── Reset on close ────────────────────────────────────────────────────────

  const handleClose = useCallback(() => {
    setShowAddForm(false);
    setNewTypeName('');
    setNewTypeFamily('');
    setEditTarget(null);
    setSearchTerm('');
    onClose();
  }, [onClose]);

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          Manage Equipment Types
          <IconButton aria-label="close" onClick={handleClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pb: 4, pt: 2, height: 600, overflowY: 'auto' }}>
          <Box display="flex" gap={2} mb={2} mt={1}>
            <TextField
              placeholder="Search by name or family..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: theme.palette.text.secondary }} /></InputAdornment>,
                endAdornment: <InputAdornment position="end"><IconButton onClick={handleSearch} edge="end"><SearchIcon sx={{ color: theme.palette.text.secondary }} /></IconButton></InputAdornment>,
              }}
              sx={{ flexGrow: 1 }}
            />
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setShowAddForm(true)}
              disabled={showAddForm} sx={gradientButtonSx}>
              Add New
            </Button>
          </Box>

          {showAddForm && (
            <Box display="flex" gap={2} mb={2}>
              <TextField fullWidth label="Type Name" value={newTypeName} onChange={(e) => setNewTypeName(e.target.value)} />
              <TextField select fullWidth label="Family" value={newTypeFamily} onChange={(e) => setNewTypeFamily(e.target.value)}>
                {FAMILY_OPTIONS.map((opt) => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
              </TextField>
              <Button variant="contained" sx={gradientButtonSx} onClick={handleAdd}
                disabled={!newTypeName.trim() || !newTypeFamily.trim()}>Save</Button>
              <Button variant="outlined" onClick={() => { setShowAddForm(false); setNewTypeName(''); setNewTypeFamily(''); }}>Cancel</Button>
            </Box>
          )}

          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="h6">Equipment Types ({meta.total})</Typography>
                <TablePagination component="div" count={meta.total} page={meta.page - 1}
                  onPageChange={handlePageChange} rowsPerPage={meta.size}
                  onRowsPerPageChange={handleRowsPerPageChange}
                  rowsPerPageOptions={[5, 10, 15, 20]} labelRowsPerPage="Per page" />
              </Box>

              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={32} sx={{ color: '#667eea' }} />
                </Box>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: theme.palette.background.paper }}>
                        {['Name', 'Family', 'Actions'].map((h) => (
                          <TableCell key={h} align={h === 'Actions' ? 'right' : 'left'}
                            sx={{ fontWeight: 600, color: theme.palette.text.secondary }}>{h}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {typesList.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={3} align="center" sx={{ py: 4, borderBottom: 'none' }}>
                            <LocalShippingIcon sx={{ fontSize: 40, color: theme.palette.text.disabled, mb: 1 }} />
                            <Typography variant="h6" color="text.secondary">No equipment types found</Typography>
                            <Typography variant="body2" color="text.secondary">
                              {searchTerm ? 'Try adjusting your search' : 'No types have been added yet'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        typesList.map((type) => (
                          <EquipmentTypeRow
                            key={type.id}
                            type={type}
                            isEditing={editTarget?.id === type.id}
                            editTarget={editTarget}
                            familyOptions={FAMILY_OPTIONS}
                            onEditChange={(field, value) =>
                              setEditTarget((prev) => prev ? { ...prev, [field]: value } : prev)
                            }
                            onStartEdit={() => setEditTarget(type)}
                            onSaveEdit={handleUpdate}
                            onCancelEdit={() => setEditTarget(null)}
                            onDelete={() => handleDeleteRequest(type.id)}
                          />
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              <TablePagination component="div" count={meta.total} page={meta.page - 1}
                onPageChange={handlePageChange} rowsPerPage={meta.size}
                onRowsPerPageChange={handleRowsPerPageChange}
                rowsPerPageOptions={[5, 10, 15, 20]} labelRowsPerPage="Per page" />
            </CardContent>
          </Card>
        </DialogContent>
      </Dialog>

      <ConfirmDialog open={confirmState.open} onClose={closeConfirm} onConfirm={executeConfirm}
        title="Confirm Delete" message={confirmState.message} />
    </>
  );
};

// ── EquipmentTypeRow ──────────────────────────────────────────────────────────

interface RowProps {
  type: EquipmentType;
  isEditing: boolean;
  editTarget: EditTarget;
  familyOptions: readonly string[];
  onEditChange: (field: keyof EquipmentType, value: string) => void;
  onStartEdit: () => void;
  onSaveEdit: () => void;
  onCancelEdit: () => void;
  onDelete: () => void;
}

const EquipmentTypeRow: React.FC<RowProps> = React.memo(({
  type, isEditing, editTarget, familyOptions,
  onEditChange, onStartEdit, onSaveEdit, onCancelEdit, onDelete,
}) => (
  <TableRow hover>
    <TableCell>
      {isEditing
        ? <TextField size="small" value={editTarget?.equipmentName ?? ''}
            onChange={(e) => onEditChange('equipmentName', e.target.value)} />
        : type.equipmentName}
    </TableCell>
    <TableCell>
      {isEditing
        ? <TextField select size="small" value={editTarget?.equipmentTypeFamily ?? ''}
            onChange={(e) => onEditChange('equipmentTypeFamily', e.target.value)} sx={{ minWidth: 160 }}>
            {familyOptions.map((opt) => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
          </TextField>
        : type.equipmentTypeFamily}
    </TableCell>
    <TableCell align="right">
      {isEditing ? (
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          <Button size="small" variant="contained" sx={gradientButtonSx} onClick={onSaveEdit}>Save</Button>
          <Button size="small" variant="outlined" onClick={onCancelEdit}>Cancel</Button>
        </Box>
      ) : (
        <>
          <IconButton size="small" onClick={onStartEdit} aria-label={`Edit ${type.equipmentName}`}>
            <EditIcon sx={{ color: '#667eea' }} />
          </IconButton>
          <IconButton size="small" onClick={onDelete} aria-label={`Delete ${type.equipmentName}`}>
            <DeleteIcon sx={{ color: '#ef4444' }} />
          </IconButton>
        </>
      )}
    </TableCell>
  </TableRow>
));

EquipmentTypeRow.displayName = 'EquipmentTypeRow';

export default ManageEquipmentTypesPopUp;